import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useGetVehicleMovementHistoryQuery, useLazyExportMovementHistoryQuery } from '@/store/api/movementHistoryApi';
import { downloadBlob } from '@/utils/helpers';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@/config/constants';

interface VehicleMovementHistoryProps {
  vehicleId: number | string;
}

const VehicleMovementHistory: React.FC<VehicleMovementHistoryProps> = ({ vehicleId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [isExporting, setIsExporting] = useState(false);
  
  // Fetch movement history data for the specific vehicle
  const { data, isLoading, error } = useGetVehicleMovementHistoryQuery(vehicleId.toString());
  
  // Export functionality
  const [exportMovementHistory, exportResult] = useLazyExportMovementHistoryQuery();

  // Handle export
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const filters: any = {
        vehicle_id: vehicleId.toString(),
        is_authorized: filterStatus === 'all' ? undefined : filterStatus === 'authorized',
        start_date: dateRange[0] ? format(dateRange[0], 'yyyy-MM-dd') : undefined,
        end_date: dateRange[1] ? format(dateRange[1], 'yyyy-MM-dd') : undefined
      };
      
      const result = await exportMovementHistory({
        filters,
        format: 'excel'
      });
      
      if (result.data) {
        downloadBlob(result.data, `vehicle_movement_history_${vehicleId}.xlsx`);
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Calculate pagination
  const movements = data?.results || [];
  const totalCount = data?.count || 0;
  
  // Get current page of data
  const displayedRows = movements;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load movement history. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Vehicle Movement History
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Track all movements of this vehicle across different locations.
        </Typography>
      </Box>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="movement-status-label">Movement Status</InputLabel>
              <Select
                labelId="movement-status-label"
                value={filterStatus}
                label="Movement Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Movements</MenuItem>
                <MenuItem value="authorized">Authorized Only</MenuItem>
                <MenuItem value="unauthorized">Unauthorized Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={dateRange[0]}
                onChange={(date) => setDateRange([date, dateRange[1]])}
                slotProps={{ 
                  textField: { size: 'small', fullWidth: true},
                  actionBar: {
                    actions: ['clear'],
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={dateRange[1]}
                onChange={(date) => setDateRange([dateRange[0], date])}
                slotProps={{ 
                  textField: { size: 'small', fullWidth: true },
                  actionBar: {
                    actions: ['clear'],
                  },
               }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date & Time</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Transfer Reference</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedRows.map((movement) => (
              <TableRow key={movement.id}>
                <TableCell>
                  {format(new Date(movement.timestamp), 'MMM dd, yyyy HH:mm')}
                </TableCell>
                <TableCell>
                  {movement.location_detail?.name || 'Unknown Location'}
                </TableCell>
                <TableCell>
                  {movement.is_authorized ? (
                    <Typography
                      component="span"
                      sx={{
                        color: 'success.main',
                        backgroundColor: 'success.light',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                      }}
                    >
                      Authorized
                    </Typography>
                  ) : (
                    <Typography
                      component="span"
                      sx={{
                        color: 'warning.main',
                        backgroundColor: 'warning.light',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                      }}
                    >
                      Unauthorized
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {movement.transfer_request_detail ? (
                    movement.transfer_request_detail.transfer_number
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No Transfer
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {displayedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                    No movement history found for this vehicle.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Movement history information">
              <IconButton size="small" sx={{ mr: 1 }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="textSecondary">
              <strong>Authorized movements</strong> are those associated with approved transfer requests, while 
              <strong> unauthorized movements</strong> are those that occurred without an associated transfer request.
            </Typography>
          </Box>
          <Box>
            <Button 
              variant="contained" 
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              disabled={movements.length === 0 || isExporting}
            >
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          </Box>
        </Box>
        <TablePagination
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default VehicleMovementHistory;